import { useEffect, useMemo, useState } from 'react';
import { callDeepLink, compareWithAppVersion } from '@croquiscom/web2app';
import { useRouter } from 'next/router';

import { checkAppVersionAndRedirectToStoreHome } from '@common/app-manager';
import { useCatalogProductIdParams, usePdpBaseInfo, usePdpConfig } from '@domains/pdp-common';

import { useShopLog } from '../log/useShopLog';
import { useGetShopStoryQuery } from '../query/useShopStory';

interface ShopServiceProps {
  shouldRefetch: boolean;
}

export const useShopService = ({ shouldRefetch }: ShopServiceProps) => {
  const { isZigzagApp } = usePdpConfig();
  const router = useRouter();

  const { clickStore, clickStory, clickStoreHome } = useShopLog();
  const params = useCatalogProductIdParams();
  const { product, shop, brand_home } = usePdpBaseInfo(params);

  const item = brand_home ?? shop;

  const { has_story, refetch } = useGetShopStoryQuery(item.id, false);

  const [show_placeholder_image, setShowPlaceholderImage] = useState(false);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch]);

  const handleImageError = () => {
    setShowPlaceholderImage(true);
  };

  const goToShopDetail = () => {
    clickStore(product.id, item.id);

    if (isZigzagApp) {
      checkAppVersionAndRedirectToStoreHome(item.id, item.main_domain);
    } else {
      if (item.main_domain) {
        clickStoreHome(product?.id, item?.id);
        router.push(`/${item.main_domain}?pdp_product_id=${product.id}`);
      }
    }
  };

  const goToStory = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (isZigzagApp && has_story?.has_story && compareWithAppVersion('7.55.0')) {
      clickStory(shop.id);
      callDeepLink('/story')({
        shop_id: item.id,
      });
    }
  };

  const category_list = useMemo(() => {
    if (!shop.trait || brand_home) {
      return null;
    }
    const { age10, age20early, age20late, age20mid, age30early, age30late, age30mid, style } = shop.trait;
    const ageList: string[] = [];

    if (age10) {
      ageList.push('10대');
    }
    if (age20early || age20late || age20mid) {
      ageList.push('20대');
    }
    if (age30early || age30late || age30mid) {
      ageList.push('30대');
    }

    return ageList.concat((style || '').split(',')).filter(Boolean);
  }, [shop.trait, brand_home]);

  return {
    show_placeholder_image,
    has_story,
    item,
    category_list,
    isZigzagApp: isZigzagApp,
    isMileageShop: shop.is_mileage_shop,
    handleImageError,
    goToShopDetail,
    goToStory,
  };
};
